import {
  isBackgroundBlurEnabled,
  parseBackgroundBlurStrength,
  PreferenceKey,
  usePreferencesStore,
  BACKGROUND_BLUR_STRENGTH_DEFAULT,
} from '@/entities/preference'

import type { BackgroundBlurPipelineHandle } from './create-background-blur-pipeline'
import { isBackgroundBlurSupported } from './is-background-blur-supported'

type BlurBinding = {
  /** Stream fed into MediaStreamTrackProcessor (often a clone). */
  pipelineInputStream: MediaStream
  /**
   * Camera stream to put back on the sender when blur is disabled.
   * Must stay live for the whole blur session — tracks that were consumed by
   * MediaStreamTrackProcessor often stall for seconds after the processor stops.
   */
  restoreStream: MediaStream
  /** Processed (canvas) stream returned to the call session. */
  processedStream: MediaStream
  handle: BackgroundBlurPipelineHandle
}

const bindingsByProcessedId = new Map<string, BlurBinding>()
/** Keeps restoreStream capturing while blur is on (unused camera tracks are throttled). */
const restoreKeepAliveByProcessedId = new Map<string, HTMLVideoElement>()

const findBindingForStream = (stream: MediaStream): { id: string, binding: BlurBinding } | undefined => {
  const direct = bindingsByProcessedId.get(stream.id)
  if (direct) {
    return { id: stream.id, binding: direct }
  }

  const trackId = stream.getVideoTracks()[0]?.id
  if (!trackId) return undefined

  for (const [id, binding] of bindingsByProcessedId.entries()) {
    const processedTrackId = binding.processedStream.getVideoTracks()[0]?.id
    if (processedTrackId === trackId) {
      return { id, binding }
    }
  }

  return undefined
}

const stopRestoreKeepAlive = (processedStreamId: string) => {
  const video = restoreKeepAliveByProcessedId.get(processedStreamId)
  if (!video) return
  video.pause()
  video.srcObject = null
  video.remove()
  restoreKeepAliveByProcessedId.delete(processedStreamId)
}

/**
 * Unused getUserMedia tracks are throttled by Chromium. Attach to a hidden video
 * so restore after blur disable has frames immediately (avoids remote freeze on last frame).
 */
export const keepRestoreStreamWarm = (restoreStream: MediaStream): HTMLVideoElement => {
  const video = document.createElement('video')
  video.muted = true
  video.defaultMuted = true
  video.autoplay = true
  video.playsInline = true
  video.setAttribute('playsinline', 'true')
  video.setAttribute('aria-hidden', 'true')
  Object.assign(video.style, {
    position: 'fixed',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
    left: '-10000px',
    top: '0',
  })
  video.srcObject = restoreStream
  document.body.appendChild(video)
  void video.play().catch(() => undefined)
  return video
}

export const getBackgroundBlurBinding = (stream: MediaStream) =>
  findBindingForStream(stream)?.binding

export const isBackgroundBlurProcessedStream = (stream: MediaStream) =>
  Boolean(findBindingForStream(stream))

export const forEachBackgroundBlurBinding = (
  cb: (binding: BlurBinding) => void,
) => {
  bindingsByProcessedId.forEach(cb)
}

/**
 * Stops the blur pipeline and returns the stream that should replace it on the sender.
 * Stops the pipeline input clone when it differs from the restore stream.
 */
export const detachBackgroundBlurProcessor = (stream: MediaStream): MediaStream | undefined => {
  const found = findBindingForStream(stream)
  if (!found) return undefined

  const { id, binding } = found
  stopRestoreKeepAlive(id)
  binding.handle.stop()
  bindingsByProcessedId.delete(id)

  if (binding.pipelineInputStream.id !== binding.restoreStream.id) {
    binding.pipelineInputStream.getTracks().forEach((track) => {
      track.stop()
    })
  }

  return binding.restoreStream
}

/**
 * Drop pipelines that finished while localVideoStream still points at the raw camera
 * (toggle/disable during apply). Returns the restore stream when any binding was removed.
 */
export const detachBackgroundBlurPipelinesForRestoreStream = (
  restoreStream: MediaStream,
): MediaStream | undefined => {
  let detached: MediaStream | undefined
  const processedIds = [...bindingsByProcessedId.entries()]
    .filter(([, binding]) => binding.restoreStream.id === restoreStream.id)
    .map(([id]) => id)

  for (const processedId of processedIds) {
    const binding = bindingsByProcessedId.get(processedId)
    if (!binding) continue
    detached = detachBackgroundBlurProcessor(binding.processedStream) ?? detached
  }

  return detached
}

/** Tear down every live blur pipeline + keep-alive video (device unlock / recovery). */
export const stopAllBackgroundBlurPipelines = (): void => {
  const processedStreams = [...bindingsByProcessedId.values()].map((binding) => binding.processedStream)
  for (const processed of processedStreams) {
    const restore = detachBackgroundBlurProcessor(processed)
    restore?.getTracks().forEach((track) => {
      track.stop()
    })
  }
  // Orphan keep-alives if any binding was already removed inconsistently.
  for (const processedId of [...restoreKeepAliveByProcessedId.keys()]) {
    stopRestoreKeepAlive(processedId)
  }
}

/**
 * Stop blur pipelines that are not tied to streams currently published on a call.
 * Used when unlocking the camera for a new getUserMedia — must not kill live call blur.
 */
export const stopIdleBackgroundBlurPipelines = (keepStreamIds: Set<string>): void => {
  const toStop = [...bindingsByProcessedId.entries()].filter(([, binding]) => {
    return !keepStreamIds.has(binding.processedStream.id)
      && !keepStreamIds.has(binding.restoreStream.id)
      && !keepStreamIds.has(binding.pipelineInputStream.id)
  })

  for (const [, binding] of toStop) {
    const restore = detachBackgroundBlurProcessor(binding.processedStream)
    restore?.getTracks().forEach((track) => {
      track.stop()
    })
  }
}

export type ApplyBackgroundBlurOptions = {
  applyEffects?: boolean
  /** Original camera stream to restore when blur is turned off. */
  restoreStream?: MediaStream
}

export const maybeApplyBackgroundBlur = async (
  stream: MediaStream,
  options: ApplyBackgroundBlurOptions = {},
): Promise<MediaStream> => {
  if (options.applyEffects === false || !stream.getVideoTracks().length) {
    return stream
  }

  if (!isBackgroundBlurSupported()) {
    return stream
  }

  const preferencesStore = usePreferencesStore()
  const enabled = isBackgroundBlurEnabled(
    preferencesStore.getPreference(PreferenceKey.BACKGROUND_BLUR_ENABLED),
  )
  if (!enabled) {
    return stream
  }

  const strength = parseBackgroundBlurStrength(
    preferencesStore.getPreference(PreferenceKey.BACKGROUND_BLUR_STRENGTH)
      ?? BACKGROUND_BLUR_STRENGTH_DEFAULT,
  )

  try {
    const { createBackgroundBlurPipeline } = await import('./create-background-blur-pipeline')
    const handle = await createBackgroundBlurPipeline(stream, {
      mode: 'blur',
      blurRadius: strength,
    })
    const restoreStream = options.restoreStream ?? stream
    bindingsByProcessedId.set(handle.stream.id, {
      pipelineInputStream: stream,
      restoreStream,
      processedStream: handle.stream,
      handle,
    })
    if (options.restoreStream && options.restoreStream.id !== stream.id) {
      restoreKeepAliveByProcessedId.set(
        handle.stream.id,
        keepRestoreStreamWarm(options.restoreStream),
      )
    }
    return handle.stream
  } catch (error) {
    console.warn('[background-blur] pipeline failed, using raw camera stream', error)
    return stream
  }
}

export const updateBackgroundBlurStrengthOnActive = (strength: number) => {
  forEachBackgroundBlurBinding((binding) => {
    binding.handle.setBlurRadius(strength)
  })
}
